<template>
  <div class="homepage bg__color--white">
    <section
      class="banner color--white"
      v-if="banner"
    >
      <div class="container banner__wrapper">
        <h1
          class="title title__h1"
          v-if="translate(banner.title)"
        >
          {{ translate(banner.title) }}
        </h1>
      </div>
    </section>

    <main class="main">
      <section
        class="stats margin__b--48"
        v-if="stats && metrics.length && statistics"
      >
        <div class="container">
          <ol class="wrapper stats__wrapper padding__tb--24">
            <li
              class="stats__li"
              v-for="(metric, key) in metrics"
              :key="`metric_${key}`"
            >
              <strong class="stats__value">
                <app-date
                  v-if="metric.type === 'date' && metric.date"
                  :timestamp="metric.date"
                  filter="differenceInCalendarDays"
                />

                <app-money
                  v-else-if="metric.type === 'amount_collected'"
                  :amount="statistics[metric.type] || 0"
                />

                <template v-else-if="metric.type === 'custom'">
                  {{ translate(metric.value) || 0 }}
                </template>

                <template v-else-if="metric.type === 'm2'">
                  {{ m2 }}
                </template>

                <template v-else>
                  {{ statistics[metric.type] || 0 }}
                </template>
              </strong>

              {{ translate(metric.text) }}
            </li>
          </ol>
        </div>
      </section>

      <section
        class="about padding__tb--24 align--center"
        v-if="about"
      >
        <div class="container">
          <div class="wrapper">
            <h2
              class="title__h2"
              v-if="translate(about.title)"
            >
              {{ translate(about.title) }}
            </h2>

            <app-content
              class="margin__t--24"
              :content="translate(about.text)"
            />
          </div>
        </div>
      </section>

      <section
        id="artwork"
        class="artwork padding__tb--24 align--center"
        v-if="artwork && statistics"
      >
        <div class="container">
          <div class="wrapper">
            <h2
              class="title__h2"
              v-if="translate(artwork.title)"
            >
              {{ translate(artwork.title) }}
            </h2>

            <app-content
              class="artwork__text margin__t--24"
              :content="translate(artwork.text)"
            />
          </div>

          <div class="artwork__container margin__t--18">
            <div class="artwork__wrapper">
              <img
                ref="artwork_image"
                class="artwork__image"
                width="1280"
                :src="translate(artwork.image)"
                alt=""
              >

              <div
                class="artwork__grid"
              >
                <template
                  v-for="n in limit_of_projects"
                  :key="n"
                >
                  <button
                    class="artwork__square artwork__square--active"
                    @click="paged = n"
                    v-if="n <= statistics.projects_count"
                  />

                  <div
                    class="artwork__square"
                    v-else
                  />
                </template>
              </div>
            </div>
          </div>

          <div class="wrapper margin__t--18">
            <app-content :content="translate(artwork.text_2)" />

            <app-link
              class="bold btn btn__color--ter btn__size--classic margin__t--24 cta"
              :link="artwork.cta"
            />
          </div>
        </div>
      </section>

      <section
        class="how_it_works padding__t--48"
        v-if="how_it_works"
      >
        <div class="container">
          <ol
            class="how_it_works__ol"
            v-if="how_it_works.steps?.length"
          >
            <li
              class="how_it_works__li"
              v-for="({ icon, title, text }, index) in how_it_works.steps"
              :key="`step_${index}`"
            >
              <img
                :src="translate(icon)"
                aria-hidden="true"
                class="margin__b--18"
                height="50"
                v-if="translate(icon)"
              >

              <h3
                class="title__h3"
                v-if="translate(title)"
              >
                {{ translate(title) }}
              </h3>

              <app-content
                class=" margin__t--12"
                :content="translate(text)"
              />
            </li>
          </ol>

          <div class="wrapper margin__t--24 align--center">
            <app-content :content="translate(how_it_works.text)" />

            <app-link
              class="bold btn btn__color--ter btn__size--classic margin__t--24 cta"
              :link="how_it_works.cta"
            />
          </div>
        </div>
      </section>
    </main>

    <section
      class="artists bg__color--ter padding__tb--48"
      v-if="artists"
    >
      <div class="container">
        <div class="artists__wrapper">
          <div class="artists__content bg__color--white">
            <h2
              class="title__h2 bold artists__title"
              v-if="translate(artists.title)"
            >
              {{ translate(artists.title) }}
            </h2>

            <ul
              class="margin__t--12"
              v-if="artists.items.length"
            >
              <li
                class="artists__li"
                v-for="({ title, text, link }, index) in artists.items"
                :key="`artist_${index}`"
              >
                <h3
                  class="bold"
                  v-if="translate(title)"
                >
                  {{ translate(title) }}
                </h3>

                <app-content :content="translate(text)" />

                <app-link
                  class="link__color--black-ter bold"
                  :link="link"
                />
              </li>
            </ul>
          </div>

          <figure class="artists__figure bg__color--sec">
            <app-video-embed
              class="artists__media"
              :src="translate(artists.media.video)"
              :params="{ controls: 0 }"
              v-if="artists.media.type === 'video' && !!translate(artists.media.video)"
            />

            <img
              class="artists__media"
              :src="translate(artists.media.image)"
              :alt="translate(artists.title)"
              v-else-if="artists.media.type === 'image' && !!translate(artists.media.image)"
            >

            <fa-icon
              class="no-thumb"
              :icon="['far', 'image']"
              v-else
            />
          </figure>
        </div>

        <div
          class="align--center margin__t--24"
          v-if="translate(artists.cta.text)"
        >
          <app-link
            class="btn btn__size--classic btn__color--sec bold cta"
            :link="artists.cta"
          />
        </div>
      </div>
    </section>

    <section
      class="testimonials padding__tb--48"
      v-if="testimonials"
    >
      <div class="container align--center">
        <h2
          class="title__h2 margin__b--18"
          v-if="translate(testimonials.title)"
        >
          {{ translate(testimonials.title) }}
        </h2>

        <app-slides v-if="testimonials.quotes.length">
          <article
            class="glider__slide"
            v-for="({ avatar, quote }, index) in testimonials.quotes"
            :key="'testimonial_' + index"
          >
            <div class="wrapper testimonials__quote">
              <figure
                class="testimonials__avatar"
                aria-hidden="true"
              >
                <img
                  class="testimonials__image"
                  :src="translate(avatar)"
                  v-if="translate(avatar)"
                >

                <fa-icon
                  class="no-thumb"
                  :icon="['far', 'user']"
                  v-else
                />
              </figure>

              <app-content
                tag="blockquote"
                :content="translate(quote)"
              />
            </div>
          </article>
        </app-slides>

        <app-link
          class="btn btn__size--classic btn__color--ter bold cta d-inline-block margin__t--24"
          :link="testimonials.cta"
        />
      </div>
    </section>

    <section
      class="campaign padding__tb--48"
      v-if="campaign"
    >
      <div class="container">
        <h2
          class="title__h2 margin__b--24 align--center"
          v-if="translate(campaign.title)"
        >
          {{ translate(campaign.title) }}
        </h2>

        <div class="campaign__wrapper">
          <div>
            <app-content :content="translate(campaign.text)" />

            <app-link
              class="btn btn__size--classic btn__color--ter bold cta d-inline-block margin__t--24"
              :link="campaign.cta"
            />
          </div>

          <figure class="campaign__figure bg__color--ter">
            <img
              class="campaign__image"
              :src="translate(campaign.image)"
              :alt="translate(campaign.title)"
              v-if="translate(campaign.image)"
            >

            <fa-icon
              class="no-thumb"
              :icon="['far', 'image']"
              v-else
            />
          </figure>
        </div>
      </div>
    </section>

    <section
      class="actions"
      v-if="actions"
    >
      <div class="container actions__container">
        <h2
          class="title__h2 margin__b--24 align--center"
          v-if="translate(actions.title)"
        >
          {{ translate(actions.title) }}
        </h2>

        <app-slides
          class="actions__slider"
          :options="actions_options"
          v-if="actions_options && actions.projects.length"
        >
          <article
            class="glider__slide actions__slide"
            v-for="({ image, title, name, city }, index) in actions.projects"
            :key="'project_' + index"
          >
            <figure class="actions__figure">
              <img
                class="actions__image"
                :src="translate(image)"
                v-if="translate(image)"
              >

              <fa-icon
                class="no-thumb"
                :icon="['far', 'image']"
                v-else
              />
            </figure>

            <h3
              class="margin__t--12"
              v-if="translate(title)"
            >
              {{ translate(title) }}
            </h3>

            <ul
              class="actions__infos margin__t--12"
              v-if="translate(name) || translate(city)"
            >
              <li v-if="translate(name)">
                <fa-icon
                  :icon="['fas', 'user']"
                  class="actions__icon"
                  fixed-width
                />

                {{ translate(name) }}
              </li>

              <li v-if="translate(city)">
                <fa-icon
                  :icon="['fas', 'location-dot']"
                  class="color--sec actions__icon"
                  fixed-width
                />

                {{ translate(city) }}
              </li>
            </ul>
          </article>
        </app-slides>

        <nav class="actions__nav margin__t--30">
          <button
            ref="actions_prev"
            class="actions__arrow"
            :aria-label="t('literal.previous')"
          >
            <fa-icon :icon="['fal', 'chevron-left']" />
          </button>

          <nav id="actions__dots" />

          <button
            ref="actions_next"
            class="actions__arrow"
            :aria-label="t('literal.next')"
          >
            <fa-icon :icon="['fal', 'chevron-right']" />
          </button>
        </nav>
      </div>
    </section>

    <section
      class="making_of bg__color--prim padding__tb--48"
      v-if="making_of"
    >
      <div class="container">
        <h2
          class="title__h2 margin__b--24 align--center"
          v-if="translate(making_of.title)"
        >
          {{ translate(making_of.title) }}
        </h2>

        <app-video-embed
          class="making_of__video"
          :src="translate(making_of.video)"
          :params="{ controls: 0 }"
          v-if="making_of.type === 'video' && !!translate(making_of.video)"
        />

        <app-slides
          class="making_of__slider"
          :options="making_of_options"
          v-else-if="making_of_options && making_of.images.length"
        >
          <div
            class="glider__slide making_of__slide"
            v-for="({ image }, index) in making_of.images"
            :key="'image_' + index"
          >
            <figure class="making_of__figure">
              <img
                class="making_of__image"
                :src="translate(image)"
                v-if="translate(image)"
              >

              <fa-icon
                class="no-thumb"
                :icon="['far', 'image']"
                v-else
              />
            </figure>
          </div>
        </app-slides>

        <div
          class="margin__t--24"
          :class="{ 'wrapper': making_of.type === 'video', 'making_of__nav': making_of.type === 'images' }"
        >
          <div v-if="making_of.type === 'images'">
            <button
              ref="making_of_prev"
              class="making_of__arrow"
              :aria-label="t('literal.previous')"
            >
              <fa-icon :icon="['fal', 'chevron-left']" />
            </button>
          </div>

          <app-content
            class="align--center"
            :content="translate(making_of.text)"
          />

          <div
            class="align--right"
            v-if="making_of.type === 'images'"
          >
            <button
              ref="making_of_next"
              class="making_of__arrow"
              :aria-label="t('literal.next')"
            >
              <fa-icon :icon="['fal', 'chevron-right']" />
            </button>
          </div>
        </div>
      </div>
    </section>

    <section
      class="partners padding__tb--18"
      v-if="partners"
    >
      <div class="container">
        <h2
          class="title__h2 margin__b--18 align--center"
          v-if="translate(partners.title)"
        >
          {{ translate(partners.title) }}
        </h2>

        <div class="wrapper">
          <app-content
            class="align--center"
            :content="translate(partners.text)"
          />
        </div>

        <app-slides
          class="partners__slider"
          :options="partners_options"
          v-if="partners_options && partners.sponsors.length"
        >
          <div
            class="glider__slide partners__slide"
            v-for="({ logo, name, link }, index) in partners.sponsors"
            :key="'sponsor_' + index"
          >
            <a
              class="partners__link"
              :href="translate(link)"
              target="_blank"
              v-if="translate(link)"
            >
              <img
                class="partners__logo"
                :src="translate(logo)"
                :alt="translate(name)"
              >
            </a>

            <img
              class="partners__logo"
              :src="translate(logo)"
              :alt="translate(name)"
              v-else
            >
          </div>
        </app-slides>

        <nav class="actions__nav margin__t--30">
          <button
            ref="partners_prev"
            class="partners__arrow"
            :aria-label="t('literal.previous')"
          >
            <fa-icon :icon="['fal', 'chevron-left']" />
          </button>

          <nav id="partners__dots" />

          <button
            ref="partners_next"
            class="partners__arrow"
            :aria-label="t('literal.next')"
          >
            <fa-icon :icon="['fal', 'chevron-right']" />
          </button>
        </nav>
      </div>
    </section>

    <app-modal
      class="modal"
      size="sm"
      @close="paged = null"
      v-if="project || loader"
    >
      <template #body>
        <div
          class="d-flex justify-content-center align-content-center padding__tb--48"
          v-if="loader"
        >
          <app-spinner />
        </div>

        <template v-else>
          <h4
            class="title__h4"
            v-if="translate(artwork.modal.title)"
          >
            {{ interpolate(artwork.modal.title, { ...onwer }) }}
          </h4>

          <app-content
            class="margin__t--6"
            :content="translate(artwork.modal.text)"
          />

          <router-link
            :to="{ name: 'Project', params: { projectSlug: project.slug } }"
            class="btn btn__size--classic btn__color--sec margin__t--12 cta"
            :aria-label="project.name"
            v-if="translate(artwork.modal.cta)"
          >
            {{ translate(artwork.modal.cta) }}
          </router-link>
        </template>
      </template>
    </app-modal>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useClientStore } from '@/stores/ClientStore'
import { useProjectStore } from '@/stores/ProjectStore'
import { useI18n } from '@/vendors/i18n'
import { scrollTo } from '@/utils/utils'

const AppLink = defineAsyncComponent(() => import('&/atoms/AppLink'))
const AppContent = defineAsyncComponent(() => import('&/atoms/AppContent'))
const AppMoney = defineAsyncComponent(() => import('&/atoms/AppMoney'))
const AppDate = defineAsyncComponent(() => import('&/atoms/AppDate'))
const AppVideoEmbed = defineAsyncComponent(() => import('&/atoms/AppVideoEmbed'))
const AppSpinner = defineAsyncComponent(() => import('&/atoms/AppSpinner'))
const AppModal = defineAsyncComponent(() => import('&/organisms/AppModal'))
const AppSlides = defineAsyncComponent(() => import('&/organisms/AppSlides'))

const { sections } = defineProps({
  sections: { type: Object, required: true }
})

const { t, translate, interpolate } = useI18n()
const route = useRoute()
const { getStatistics } = useClientStore()
const { getProjects } = useProjectStore()

const { settings, banner, stats, about, artwork, how_it_works, artists, testimonials, campaign, actions, making_of, partners } = sections.attributes

const { campaign_id = [], limit_of_projects = 0 } = settings
const { metrics = [] } = stats

const paged = ref()

const actions_options = ref()
const actions_prev = ref()
const actions_next = ref()

const making_of_options = ref()
const making_of_prev = ref()
const making_of_next = ref()

const partners_options = ref()
const partners_prev = ref()
const partners_next = ref()

const { statistics } = getStatistics({ query: stats.query, reload: true })
const { projects, loader } = getProjects({ query: computed(() => ({ campaign_id, page: paged.value, per_page: 1, sort_by: 'created_at_last' })), dependencies: [paged] })

const m2 = computed(() => statistics.value?.projects_count ? parseInt(limit_of_projects) - statistics.value.projects_count : parseInt(limit_of_projects))

const background = computed(() => translate(banner.image) ? `url(${translate(banner.image)})` : '')
const project = computed(() => projects.value?.[0])
const onwer = computed(() => projects.value?.[0].users?.[0])

onMounted(() => {
  if (route.hash) setTimeout(() => scrollTo(route.hash), 100)

  actions_options.value = {
    slidesToShow: 1,
    dots: '#actions__dots',
    arrows: { prev: actions_prev.value, next: actions_next.value },
    responsive: [
      { breakpoint: 991, settings: { slidesToShow: 3 } },
      { breakpoint: 575, settings: { slidesToShow: 2 } }
    ]
  }

  making_of_options.value = {
    slidesToShow: 1,
    arrows: { prev: making_of_prev.value, next: making_of_next.value },
    responsive: [
      { breakpoint: 991, settings: { slidesToShow: 3 } },
      { breakpoint: 575, settings: { slidesToShow: 2 } }
    ]
  }

  partners_options.value = {
    slidesToShow: 1,
    dots: '#partners__dots',
    arrows: { prev: partners_prev.value, next: partners_next.value },
    responsive: [
      { breakpoint: 991, settings: { slidesToShow: 3 } },
      { breakpoint: 575, settings: { slidesToShow: 2 } }
    ]
  }
})
</script>

<style lang="scss" scoped>
$black: #131415;
$color--primary: #86C55C;
$color--secondary: #377137;

.title {
  &__h1 {
    font-size: rem(24px);
    line-height: 1.17;

    @include mq(sm) {
      font-size: rem(48px);
    }

    @include mq(lg) {
      font-size: rem(64px);
    }
  }

  &__h2 {
    font-size: rem(30px);

    @include mq(sm) {
      font-size: rem(48px);
    }

    @include mq(lg) {
      font-size: rem(72px);
    }
  }

  &__h3 {
    font-size: rem(24px);
    font-weight: 700;
    line-height: 1.1;

    @include mq(sm) {
      font-size: rem(36px);
    }
  }

  &__h4 {
    font-size: rem(18px);
    font-weight: 700;
    line-height: 1.1;

    @include mq(sm) {
      font-size: rem(24px);
    }
  }
}

.no-thumb {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  color: $white;
  font-size: rem(72px);
}

.cta,
:deep(.cta) {
  display: inline-block;
  font-size: rem(18px);
  line-height: 1.5;
  font-family: $font-family--secondary;
  border-radius: 6px;
}

.wrapper {
  @include mq(md) {
    margin-left: 48px;
    margin-right: 48px;
  }

  @include mq(xl) {
    margin-left: 106px;
    margin-right: 106px;
  }
}

:deep() {
  .glider {
    &-prev,
    &-next {
      color: $color--secondary;
    }

    &-dots {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;
      height: 26px;

      @include mq(sm) {
        gap: 18px
      }
    }

    &-dot {
      background: $color--primary;
      width: 17px;
      height: 17px;
      border-radius: 50%;
      transition: all .2s ease-in-out;

      &:hover {
        background: $color--secondary;
      }

      &.active {
        background: #694E9F;
      }
    }
  }
}

.homepage {
  color: $black;
  font-size: rem(18px);
  position: relative;
  overflow: hidden;
}

:deep() {
  .modal {
    &__container {
      max-width: 534px;
      border-radius: 10px;
      background: $color--tertiary;
    }

    &__body {
      padding: 48px;
      font-size: rem(18px);

      @include mq(sm) {
        font-size: rem(20px);
      }
    }
  }
}

.banner {
  position: relative;
  font-size: rem(18px);
  background-position: right -180px bottom -20px;
  background-repeat: no-repeat;
  background-size: auto 100%;
  background-color: $color--secondary;
  transition: all .2s ease-in-out;
  text-shadow: 2px 2px 2px $color--secondary;

  &__wrapper {
    position: relative;
    z-index: 10;
    padding-top: 72px;
    padding-bottom: 72px;

    @include mq(sm) {
      padding-top: 100px;
      padding-bottom: 170px;
    }

    @include mq(lg) {
      padding-top: 142px;
      padding-bottom: 191px;
    }

    &::before {
      content: '';
      aspect-ratio: 3463.1 / 1386.1;
      position: absolute;
      display: block;
      height: 100%;
      right: 15%;
      bottom: -10%;
      transform: translate(50%, 0);
      background-image: v-bind(background);
      background-size: 100% auto;
      background-repeat: no-repeat;
      background-position: top left;
      z-index: -1;

      @include mq(sm) {
        top: 0%;
        right: 10%;
        height: 90%;
      }

      @include mq(md) {
        right: 18%;
        height: 90%;
      }

      @include mq(lg) {
        top: -5%;
        right: 18%;
        height: 120%;
      }

      @include mq(xl) {
        right: 25%;
        height: 140%;
      }
    }
  }
}

.main {
  position: relative;

  &::before,
  &::after {
    display: block;
    z-index: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    background-repeat: no-repeat;

    @include mq(sm) {
      content: '';
      top: 70px;
      height: calc(100% - 70px);
    }
  }

  &::after {
    content: '';
  }

  &::before {
    background-position: top left;
    background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayIgdmVyc2lvbj0iMS4xIiB2aWV3Qm94PSIwIDAgMzE0IDUyNi43Ij4KICA8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMjkuMy4xLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogMi4xLjAgQnVpbGQgMTUxKSAgLS0+CiAgPGRlZnM+CiAgICA8c3R5bGU+CiAgICAgIC5zdDAgewogICAgICAgIG1hc2s6IHVybCgjbWFzayk7CiAgICAgIH0KCiAgICAgIC5zdDEgewogICAgICAgIGZpbGw6ICNkOWQ5ZDk7CiAgICAgIH0KCiAgICAgIC5zdDIgewogICAgICAgIGZpbGw6ICM3YWQxZTE7CiAgICAgIH0KCiAgICAgIC5zdDIsIC5zdDMsIC5zdDQgewogICAgICAgIGZpbGwtcnVsZTogZXZlbm9kZDsKICAgICAgfQoKICAgICAgLnN0MyB7CiAgICAgICAgZmlsbDogIzg2YzU1YzsKICAgICAgfQoKICAgICAgLnN0NCB7CiAgICAgICAgZmlsbDogI2ZiZGMyZDsKICAgICAgfQogICAgPC9zdHlsZT4KICAgIDxtYXNrIGlkPSJtYXNrIiB4PSIwIiB5PSIwIiB3aWR0aD0iMzE0IiBoZWlnaHQ9IjU5OSIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CiAgICAgIDxnIGlkPSJtYXNrMF8yMjNfNSI+CiAgICAgICAgPHJlY3QgY2xhc3M9InN0MSIgeT0iMCIgd2lkdGg9IjMxNCIgaGVpZ2h0PSI1OTkiLz4KICAgICAgPC9nPgogICAgPC9tYXNrPgogIDwvZGVmcz4KICA8ZyBjbGFzcz0ic3QwIj4KICAgIDxnPgogICAgICA8cGF0aCBjbGFzcz0ic3QzIiBkPSJNMCw0NDIuNHY4NC4zYzEyNy4zLTY1LjksMjUyLjktMTc2LjksMjk4LTM0NS43QzI2MS4zLDMwOS4zLDEyNC43LDM5My44LDAsNDQyLjRaIi8+CiAgICAgIDxwYXRoIGNsYXNzPSJzdDQiIGQ9Ik0wLDMwNi42djEzNS40YzEyNC4yLTQ4LjQsMjYwLTEzMi4zLDI5Ni41LTI1OS42LDAsMCwwLDAsMCwwLDE0LjYtNTQuNiwyMC44LTExNS4yLDE1LjctMTgyLjNoLTEzNy45QzE3Ny44LDE3Mi40LDEwMS4yLDI1NS4yLDAsMzA2LjZaIi8+CiAgICA8L2c+CiAgPC9nPgogIDxwYXRoIGNsYXNzPSJzdDIiIGQ9Ik0wLDI3Ni40djMyLjVDMTAyLDI1Ny40LDE3OS40LDE3NC4zLDE3NS45LDBoLTcyLjdDMTE2LjYsMTI4LjYsNjYsMjE2LjgsMCwyNzYuNFoiLz4KPC9zdmc+");

    @include mq(sm) {
      background-size: 213px auto;
    }

    @include mq(md) {
      background-size: 250px auto;
    }

    @include mq(lg) {
      background-size: 313px auto;
    }
  }

  &::after {
    background-size: 170px auto;
    background-position: bottom right;
    background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyBpZD0iTGF5ZXJfMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjAgMCA0NzAgNDgxIj4KICA8IS0tIEdlbmVyYXRvcjogQWRvYmUgSWxsdXN0cmF0b3IgMjkuMy4xLCBTVkcgRXhwb3J0IFBsdWctSW4gLiBTVkcgVmVyc2lvbjogMi4xLjAgQnVpbGQgMTUxKSAgLS0+CiAgPGRlZnM+CiAgICA8c3R5bGU+CiAgICAgIC5zdDAgewogICAgICAgIGZpbGw6ICM2OTRlOWY7CiAgICAgIH0KCiAgICAgIC5zdDAsIC5zdDEsIC5zdDIgewogICAgICAgIGZpbGwtcnVsZTogZXZlbm9kZDsKICAgICAgfQoKICAgICAgLnN0MSB7CiAgICAgICAgZmlsbDogIzg2YzU1YzsKICAgICAgfQoKICAgICAgLnN0MiB7CiAgICAgICAgZmlsbDogI2ZiZGMyZDsKICAgICAgfQogICAgPC9zdHlsZT4KICA8L2RlZnM+CiAgPHBhdGggY2xhc3M9InN0MSIgZD0iTTQ3MCwyMi4zQzMxNS40LDg2LjQsMTQwLjMsMjQ1LjYsMTEyLDQ2NS41YzIyLTE3MC45LDIwNS44LTI3NC42LDM1OC0zMjguMVYyMi4zWiIvPgogIDxwYXRoIGNsYXNzPSJzdDAiIGQ9Ik00NzAsMjY4Ljh2LTE2Yy04Mi40LDQ2LjUtMTYyLjksMTE4LjktMTkxLjIsMjI4LjJoOTkuMmMtLjMtNjAuMiwxNS41LTE0NS42LDkyLTIxMi4yWiIvPgogIDxwYXRoIGNsYXNzPSJzdDIiIGQ9Ik00NzAsMTMyLjJjLTE0OC43LDUxLjctMzI5LjUsMTUxLjctMzU5LjMsMzE2LS4yLDEtMS40LDguNS0xLjgsMTEuMi0xLDcuMS0xLjksMTQuMy0yLjYsMjEuNmgxNzMuNWMyOC40LTEwOC45LDEwOC4zLTE4MS4xLDE5MC4yLTIyNy42di0xMjEuMloiLz4KPC9zdmc+");

    @include mq(sm) {
      background-size: 250px auto;
    }

    @include mq(md) {
      background-size: 320px auto;
    }

    @include mq(lg) {
      background-size: 470px auto;
    }
  }

  & > * {
    position: relative;
    z-index: 10;

    &:last-child {
      padding-bottom: 72px;
    }
  }
}

.stats {
  position: relative;
  z-index: 20;
  background: $color--primary;

  @include mq(sm) {
    background: none;
    margin-top: -70px;
  }

  &__wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 32px;
    background: $color--primary;
    padding: 48px 12px;

    @include mq(sm) {
      flex-direction: row;
      align-items: flex-start;
      gap: 18px;
      border-radius: 12px;
    }

    @include mq(md) {
      padding: 48px 70px;
      gap: 32px;
    }

    @include mq(xl) {
      gap: 60px
    }
  }

  &__li {
    font-size: rem(16px);
    line-height: 1.1;
    text-align: center;

    @include mq(xl) {
      font-size: rem(18px);
    }
  }

  &__value {
    font-size: rem(36px);
    line-height: 1.14;
    margin-bottom: 4px;
    display: block;
    text-align: center;
    font-family: $font-family--secondary;

    @include mq(md) {
      font-size: rem(48px);
    }

    @include mq(lg) {
      font-size: rem(84px);
    }
  }
}

.artwork {
  &__text {
    font-size: rem(16px);
  }

  &__container {
    position: relative;
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: $color--secondary $white;
  }

  &__wrapper {
    width: 1272px;
    position: relative;
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;
  }

  &__grid {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
    gap: 0;
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    grid-template-rows: repeat(auto-fill, 1fr);
  }

  &__square {
    background-color: rgba(#fff, 0.92);
    box-sizing: border-box;
    transition: all 0.2s ease-in-out;

    &--active {
      background: none;
      border: 1px solid rgba(#fff, 0.6);

      &:hover {
        background-color: rgba(#fff, 0.6);
      }
    }
  }
}

.how_it_works {
  &__ol {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    @include mq(sm) {
      grid-template-columns: repeat(4, 1fr);
    }

    @include mq(md) {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  &__li {
    background: $color--primary;
    border-radius: 10px;
    padding: 42px 24px;

    @include mq(sm) {
      grid-column: span 2;

      &:last-child:nth-child(2n+1) {
        grid-column-end: -2;
      }
    }

    @include mq(md) {
      &:last-child:nth-child(2n+1) {
        grid-column-end: initial;
      }

      &:last-child:nth-child(3n+1) {
        grid-column-end: -3;
      }

      &:nth-last-child(2):nth-child(3n+1) {
        grid-column-end: -4;
      }
    }
  }

  &__icon {
    width: auto;
    height: 50px;
    display: block;
  }
}

.artists {
  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;

    @include mq(sm) {
      gap: 0;
      grid-template-columns: repeat(12, 1fr);
      align-items: center;

      @include mq(lg) {
        align-items: flex-end;
      }
    }
  }

  &__content {
    position: relative;
    z-index: 10;
    padding: 36px 48px;

    @include mq(sm) {
      grid-row: 1;
      grid-column: 1 / span 7;
      padding: 24px;
    }

    @include mq(md) {
      padding: 36px 48px;
    }

    @include mq(lg) {
      grid-column: 1 / span 5;
    }
  }

  &__title {
    font-size: rem(24px);
    line-height: 1;
  }

  &__li {
    &:not(:last-child) {
      margin-bottom: 30px;
    }
  }

  &__figure {
    position: relative;

    @include aspect-ratio(863, 517);

    @include mq(sm) {
      grid-row: 1;
      grid-column: 7 / span 12;
    }

    @include mq(lg) {
      grid-row: 1;
      grid-column: 5 / span 12;
      margin-bottom: 30px;
    }

    @include mq(xl) {
      margin-bottom: 60px;
    }
  }

  :deep(.artists__media),
  &__media {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.testimonials {
  &__quote {
    line-height: 1.2;
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr;
    place-items: center;
    padding: 0 50px;

    @include mq(sm) {
      grid-template-columns: 148px auto;
      text-align: left;
    }

    @include mq(md) {
      font-size: rem(24px);
      padding: 0;
    }
  }

  &__avatar {
    position: relative;
    background: $color--secondary;
    width: 148px;
    height: 148px;
    overflow: hidden;
    position: relative;
    border-radius: 50%;

    .no-thumb {
      font-size: rem(32px);
    }
  }

  &__image {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    max-width: 100%!important;
  }
}

.campaign {
  background: #FBDC2D;
  line-height: 1.16;

  @include mq(lg) {
    font-size: rem(24px);
  }

  &__wrapper {
    display: grid;
    grid-template-columns: 1fr;
    align-items: center;
    gap: 24px;

    @include mq(sm) {
      grid-template-columns: 5fr 6fr;
    }

    @include mq(lg) {
      gap: 48px;
    }

    @include mq(xl) {
      gap: 132px;
    }
  }

  &__figure {
    position: relative;

    @include aspect-ratio(624, 452);
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
  }
}

.actions {
  &__container {
    padding-top: 48px;
    padding-bottom: 85px;
    background-image: url("data:image/svg+xml;base64,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");
    background-size: auto 100%;
    background-repeat: no-repeat;
    background-position: left 40px top 0;
  }

  &__slider {
    :deep() {
      .glider-track {
        align-items: flex-start!important;
      }

      .glider-prev,
      .glider-next {
        display: none;
      }
    }
  }

  &__slide {
    @include mq(sm) {
      padding: 0 12px;
    }
  }

  &__nav {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;

    @include mq(sm) {
      gap: 24px
    }
  }

  &__arrow {
    font-size: rem(26px);
    transition: all .2s ease-in-out;

    &.disabled {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__figure {
    background: $color--secondary;

    @include aspect-ratio(407, 407);
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    max-width: 100%!important;
  }

  &__infos {
    display: flex;
    gap: 12px;

    @include mq(sm) {
      gap: 24px;
    }
  }

  &__icon {
    margin-right: 4px;
  }
}

.making_of {
  &__slide {
    @include mq(sm) {
      padding: 0 12px;
    }
  }

  &__slider {
    :deep() {
      .glider-prev,
      .glider-next {
        display: none;
      }
    }
  }

  &__figure {
    background: $color--secondary;

    @include aspect-ratio(407, 451);
  }

  &__image {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    max-width: 100%!important;
  }

  &__nav {
    display: grid;
    grid-template-columns: auto minmax(auto, 840px) auto;
    align-items: center;
    gap: 12px;

    @include mq(sm) {
      gap: 24px;
    }
  }

  &__arrow {
    font-size: rem(26px);

    &.disabled {
      opacity: 0;
      pointer-events: none;
    }
  }
}

.partners {
  &__nav {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;

    @include mq(sm) {
      gap: 24px
    }
  }

  &__slider {
    margin-top: 48px!important;

    :deep() {
      .glider-prev,
      .glider-next {
        display: none;
      }
    }
  }

  &__arrow {
    font-size: rem(26px);

    &.disabled {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__link {
    display: block;
  }

  &__logo {
    max-height: 180px;
    width: auto;
  }
}
</style>